import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Alert, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { computeEffectiveStatus } from "@/features/network-topology/engine";
import { useNetworkTopology } from "@/features/network-topology/useNetworkTopology";

/**
 * アクティブなアラートだけを表示するサイドカードリスト。
 * 参照画像のトースト型カード（左に色のサイドバー）に倣う。
 *
 * 種別:
 *  - offline ノード（manual=offline）
 *  - unreachable ノード（上流が落ちて疎通不可）
 *  - sim 連動先がアクティブ稼働中なのに offline（要重大）
 */
export function AlertPanel() {
  const { nodes } = useNetworkTopology();

  const alerts = useMemo(() => {
    const status = computeEffectiveStatus(nodes);
    const items: { id: string; severity: "warning" | "error" | "info"; message: string }[] = [];
    for (const n of nodes) {
      const s = status.get(n.id);
      if (s === "fault") {
        items.push({
          id: n.id,
          severity: "warning",
          message: `${n.label}: 通信異常を検知（オレンジ点滅）`,
        });
      } else if (s === "offline") {
        items.push({
          id: n.id,
          severity: "warning",
          message: n.simBinding ? `${n.label} offline — ${n.simBinding.kind} 系統が停止中` : `${n.label} offline`,
        });
      } else if (s === "unreachable") {
        items.push({
          id: n.id,
          severity: "info",
          message: `${n.label} unreachable (upstream offline)`,
        });
      }
    }
    return items;
  }, [nodes]);

  if (alerts.length === 0) {
    return (
      <Stack sx={{ p: 2, gap: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          Alerts
        </Typography>
        <Typography variant="caption" color="text.secondary">
          異常なし
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack sx={{ p: 2, gap: 1, maxHeight: 220, overflow: "auto" }}>
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        Alerts ({alerts.length})
      </Typography>
      {alerts.map((a) => (
        <Alert
          key={a.id}
          severity={a.severity}
          icon={<WarningAmberRoundedIcon fontSize="small" />}
          sx={{ py: 0.25, alignItems: "center" }}
        >
          {a.message}
        </Alert>
      ))}
    </Stack>
  );
}
